import '@mantine/charts/styles.css';
import { DirectionProvider, MantineProvider } from '@mantine/core';
import '@mantine/core/styles.layer.css';
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';
import '@mantine/notifications/styles.css';
import { HelmetProvider } from '@dr.pogodin/react-helmet';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  createHashRouter,
  createRoutesFromElements,
  Outlet,
  Route,
  RouterProvider,
} from 'react-router-dom';
import { detectAndSetLanguage } from '@/i18n/languageDetection';
import './assets/layout.css';
import { LanguageRoutes } from './components/Layout/LanguageRoutes';
import { SEOHead } from './components/SEO/SEOHead';
import './i18n';
import { cssVariablesResolver, themeWithCloseLabel } from './theme';

function RootLayout() {
  return (
    <>
      <Notifications position="top-right" zIndex={1000} autoClose={6000} />
      <Outlet />
    </>
  );
}

const LANGUAGE_PREFIXES = ['de', 'fr', 'es', 'pt', 'zh', 'th', 'ar'];

// A data router, so pages can block in-app navigation with useBlocker.
const router = createHashRouter(
  createRoutesFromElements(
    <Route element={<RootLayout />}>
      <Route path="/*" element={<LanguageRoutes />} />
      {LANGUAGE_PREFIXES.map((lang) => (
        <Route key={lang} path={`/${lang}/*`} element={<LanguageRoutes languagePrefix={lang} />} />
      ))}
    </Route>
  )
);

export default function App() {
  const { i18n, t } = useTranslation();
  const closeLabel = t('ui.close');
  const theme = React.useMemo(() => themeWithCloseLabel(closeLabel), [closeLabel]);

  // Determine if current language is RTL
  const isRTL = i18n.language.startsWith('ar');

  // Initialize language detection and URL sync
  React.useEffect(() => {
    detectAndSetLanguage(i18n);
  }, [i18n]);

  // Set document direction and lang attribute
  React.useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language, isRTL]);

  return (
    <HelmetProvider>
      <DirectionProvider>
        <MantineProvider
          theme={theme}
          defaultColorScheme="auto"
          cssVariablesResolver={cssVariablesResolver}
        >
          <ModalsProvider>
            <SEOHead />
            <RouterProvider router={router} />
          </ModalsProvider>
        </MantineProvider>
      </DirectionProvider>
    </HelmetProvider>
  );
}
